import { useState, useEffect, useCallback } from "react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Primitives/Card"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { Button } from "@/components/Primitives/Button"
import { Text } from "@/components/Primitives/Text"
import { Check, ArrowLeft, Trash2 } from "lucide-react"
import { getPasswordSchema } from "@/lib/schemas/auth"
import { useUser } from "@/hooks/useUser"
import { Spinner } from "@/components/Primitives/Spinner"
import { useConfig } from "@/hooks/useConfig"
import { Icon } from "@/components/Primitives"
import { deleteUserAccount, patchUserPassword } from "@/lib/api/circle"
import { saveToken } from "@/hooks/useLocalStorage"
import { setToken } from "@/lib/api/client"
import { toast } from "sonner"
import { Toaster } from "@/components/Primitives/Sonner"
import { removeApiToken } from "@/lib/api/client"
import { clearLocalStorageToken } from "@/hooks/useLocalStorage"
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/Primitives/Tabs"
import { Switch } from "@/components/Primitives/Switch/switch"
import { DeleteAccountModal } from "@/lib/components/DeleteAccountModal"
import { PasswordModal } from "@/lib/components/PasswordModal"
import { Link, useLocation } from "react-router-dom"

interface FieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  onSave: () => void
  isUpdating: boolean
  isSaved: boolean
  placeholder: string
  type?: string
  disabled?: boolean
}

function Field({
  label,
  value,
  onChange,
  onSave,
  isUpdating,
  placeholder,
  type = "text",
  disabled = false,
}: FieldProps) {
  return (
    <div>
      <Label htmlFor={label} className="font-semibold">
        {label}
      </Label>
      <div className="flex gap-2 mt-2">
        <Input
          id={label}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isUpdating || disabled}
          placeholder={placeholder}
        />
        <Button
          onClick={onSave}
          disabled={isUpdating || disabled}
          className="whitespace-nowrap"
          variant="tertiary"
        >
          {isUpdating ? (
            <Spinner className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4 text-purple-500" />
          )}
        </Button>
      </div>
    </div>
  )
}

export function ProfilePage() {
  const { t } = useConfig()
  const location = useLocation()
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [isNameSaved, setIsNameSaved] = useState(false)
  const [isEmailSaved, setIsEmailSaved] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [passwordError, setPasswordError] = useState("")
  const {
    user,
    isLoading,
    error,
    updateName,
    isUpdatingName,
    updateEmail,
    isUpdatingEmail,
    updateWantsNews,
    updateWantsQuestProgress,
    updateWantsReminders,
  } = useUser()
  const passwordSchema = getPasswordSchema(t)
  const [notifQuestProgress, setNotifQuestProgress] = useState(true)
  const [notifReminders, setNotifReminders] = useState(true)
  const [notifNewFeatures, setNotifNewFeatures] = useState(true)

  const handleToggleQuestProgress = useCallback(() => {
    updateWantsQuestProgress({ wantsQuestProgress: !notifQuestProgress })
  }, [notifQuestProgress, updateWantsQuestProgress])
  const handleToggleReminders = useCallback(() => {
    updateWantsReminders({ wantsReminders: !notifReminders })
  }, [notifReminders, updateWantsReminders])
  const handleToggleNewFeatures = useCallback(() => {
    updateWantsNews({ wantsNews: !notifNewFeatures })
  }, [notifNewFeatures, updateWantsNews])

  const allNotificationsDisabled =
    !notifQuestProgress && !notifReminders && !notifNewFeatures

  const notificationSwitches = [
    {
      label: t("config_user_profile_settings_quest_progress"),
      desc: t("config_user_profile_settings_quest_progress_desc"),
      checked: notifQuestProgress,
      onChange: handleToggleQuestProgress,
    },
    {
      label: t("config_user_profile_settings_reminders"),
      desc: t("config_user_profile_settings_reminders_desc"),
      checked: notifReminders,
      onChange: handleToggleReminders,
    },
    {
      label: t("config_user_profile_settings_new_features"),
      desc: t("config_user_profile_settings_new_features_desc"),
      checked: notifNewFeatures,
      onChange: handleToggleNewFeatures,
    },
    {
      label: t("config_user_profile_settings_account_info"),
      desc: t("config_user_profile_settings_account_info_desc"),
      checked: true,
      onChange: () => {},
      disabled: true,
    },
  ]

  useEffect(() => {
    if (user) {
      setFullName(user.full_name)
      setEmail(user.email)
      setNotifQuestProgress(user.wants_quest_progress)
      setNotifReminders(user.wants_reminders)
      setNotifNewFeatures(user.wants_news)
    }
  }, [user])

  const handleSaveName = () => {
    updateName({ fullName })
    setIsNameSaved(true)
    setTimeout(() => setIsNameSaved(false), 2000)
  }

  const handleSaveEmail = () => {
    updateEmail({ email, profileId: user!.external_id })
    setIsEmailSaved(true)
    setTimeout(() => setIsEmailSaved(false), 2000)
  }

  const handlePasswordSubmit = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    if (!currentPassword || !newPassword) {
      setPasswordError(t("config_profile_error_fields_required"))
      return
    }

    const validation = passwordSchema.safeParse(newPassword)
    if (!validation.success) {
      setPasswordError(t("config_profile_error_invalid_password"))
      return
    }

    try {
      setIsPasswordSubmitting(true)
      const token = await patchUserPassword(
        currentPassword,
        newPassword,
        user!.external_id,
      )
      saveToken(token)
      setToken(token)
      setShowPasswordModal(false)
      toast.success(t("config_profile_password_changed_success"), {
        className: "!bg-teal-700 !text-white !border !border-teal-700",
      })
      setPasswordError("")
    } catch {
      setPasswordError(t("config_profile_error_generic"))
    } finally {
      setIsPasswordSubmitting(false)
    }
  }

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true)
      await deleteUserAccount()
      clearLocalStorageToken()
      removeApiToken()
      setIsDeletingAccount(false)

      window.location.href = "https://www.pandorasway.com/"

      toast.success(t("config_profile_delete_account_success"), {
        className: "!bg-green-700 !text-white !border !border-green-700",
      })
    } catch {
      toast.error(t("config_profile_delete_account_error_generic"))
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="px-2 py-10">
            <CardContent>
              <div className="flex justify-center">
                <Spinner />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="px-2 py-10">
            <CardContent>
              <div className="flex justify-center">
                <Text variant="small" className="text-red-500">
                  {error.toString()}
                </Text>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="p-4">
          <Link to="/quests">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4text-neutral-700" />
              <Text variant="small" className="text-neutral-700">
                {t("config_profile_button_back")}
              </Text>
            </Button>
          </Link>
          <CardHeader>
            <CardTitle>
              <Text variant="paragraph" bold>
                {t("config_profile_title")}
              </Text>
              {allNotificationsDisabled && (
                <Text
                  variant="paragraph"
                  className="mt-5 lg:mb-5 border-gray-200 bg-purple-50 p-4 rounded-lg font-normal"
                >
                  {t("config_user_profile_settings_description_all_disabled")}
                </Text>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs
              defaultValue={
                location.hash === "#notifications" ? "notifications" : "details"
              }
              onValueChange={(value) => {
                window.location.hash = value
              }}
              className="w-full"
            >
              <TabsList className="w-full mb-4 overflow-x-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-purple-200 scrollbar-track-transparent flex-nowrap">
                <TabsTrigger value="details" className="flex-1">
                  {t("config_account_title")}
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex-1">
                  {t("config_notifications_title")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="details">
                <div className="grid gap-6">
                  <Field
                    label={t("config_profile_name_label")}
                    value={fullName}
                    onChange={setFullName}
                    onSave={handleSaveName}
                    isUpdating={isUpdatingName}
                    isSaved={isNameSaved}
                    placeholder={t("config_profile_name_placeholder")}
                  />
                  <Field
                    label={t("config_profile_email_label")}
                    value={email}
                    onChange={setEmail}
                    onSave={handleSaveEmail}
                    isUpdating={isUpdatingEmail}
                    isSaved={isEmailSaved}
                    placeholder={t("config_profile_email_placeholder")}
                    type="email"
                  />
                  <div>
                    <Label htmlFor="password" className="font-semibold">
                      {t("config_profile_password_label")}
                    </Label>
                    <div className="flex gap-2 mt-2 items-center">
                      <Text variant="small">
                        {t("config_profile_password_description")}
                      </Text>
                      <Button
                        onClick={() => setShowPasswordModal(true)}
                        className="whitespace-nowrap h-fit"
                        variant="tertiary"
                      >
                        {t("config_profile_button_update")}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-between gap-2 mt-8">
                  <Text variant="small" bold>
                    Danger Zone
                  </Text>
                  <Button
                    onClick={() => setShowDeleteModal(true)}
                    className="whitespace-nowrap h-fit"
                    variant="destructive"
                  >
                    <Icon icon={Trash2} className="text-white" />
                    {t("config_mission_delete_account")}
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="notifications">
                <div>
                  <Text
                    variant="small"
                    bold
                    className="mb-2 text-muted-foreground"
                  >
                    {t("config_user_profile_settings_email_title")}
                  </Text>
                  <div className="bg-white rounded-xl border p-4 flex flex-col gap-4">
                    {notificationSwitches.map((item, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between gap-4 ${
                          item.disabled ? "opacity-60" : ""
                        }`}
                      >
                        <div>
                          <Text bold>{item.label}</Text>
                          <Text
                            variant="small"
                            className="text-muted-foreground"
                          >
                            {item.desc}
                          </Text>
                        </div>
                        <div>
                          <Switch
                            checked={item.checked}
                            onCheckedChange={item.onChange}
                            disabled={item.disabled}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        <PasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onSubmit={handlePasswordSubmit}
          isUpdating={isPasswordSubmitting}
          error={passwordError}
        />
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
          isDeleting={isDeletingAccount}
        />
        <Toaster position="top-center" />
      </div>
    </div>
  )
}
